import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Users, 
  UserCheck, 
  Calendar, 
  TrendingUp, 
  MessageCircle,
  ArrowUpRight,
  ArrowDownRight,
  Plus,
  Phone,
  Clock
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard({ user, logout }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`, { withCredentials: true }),
        axios.get(`${API}/leads?limit=5`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setRecentLeads(leadsRes.data.leads || []);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      title: "Total Leads", 
      value: stats?.total_leads || 0, 
      change: `+${stats?.leads_today || 0} today`,
      positive: true,
      icon: Users, 
      color: "bg-blue-500" 
    },
    { 
      title: "Customers", 
      value: stats?.total_customers || 0, 
      change: `${stats?.conversion_rate || 0}% conversion`,
      positive: true,
      icon: UserCheck, 
      color: "bg-green-500" 
    },
    { 
      title: "Appointments", 
      value: stats?.total_appointments || 0, 
      change: `${stats?.pending_appointments || 0} pending`,
      positive: true,
      icon: Calendar, 
      color: "bg-purple-500" 
    },
    { 
      title: "New Leads", 
      value: stats?.new_leads || 0, 
      change: "Awaiting action",
      positive: true,
      icon: TrendingUp, 
      color: "bg-orange-500" 
    },
  ];

  const getStageColor = (stage) => {
    const colors = {
      new: "bg-blue-100 text-blue-800",
      engaged: "bg-yellow-100 text-yellow-800",
      appointment_booked: "bg-purple-100 text-purple-800",
      converted: "bg-green-100 text-green-800",
      lost: "bg-red-100 text-red-800"
    };
    return colors[stage] || "bg-slate-100 text-slate-800";
  };

  if (loading) {
    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-cyan-700 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6" data-testid="dashboard">
        {/* Welcome Section */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">
              Welcome back, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-slate-600 mt-1">Here&apos;s what&apos;s happening with your leads today.</p>
          </div>
          <Button 
            className="bg-cyan-700 hover:bg-cyan-800"
            onClick={() => navigate('/leads')}
            data-testid="add-lead-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Lead
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => (
            <Card key={index} className="stat-card" data-testid={`stat-${stat.title.toLowerCase().replace(' ', '-')}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">{stat.title}</p>
                    <p className="stat-value mt-2">{stat.value}</p>
                    <div className="flex items-center gap-1 mt-2">
                      {stat.positive ? (
                        <ArrowUpRight className="w-4 h-4 text-green-500" />
                      ) : (
                        <ArrowDownRight className="w-4 h-4 text-red-500" />
                      )}
                      <span className="text-sm text-slate-600">{stat.change}</span>
                    </div>
                  </div>
                  <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Leads */}
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold font-[Manrope]">Recent Leads</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/leads')}>
                View all
              </Button>
            </CardHeader>
            <CardContent>
              {recentLeads.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No leads yet. Start capturing leads from your website!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recentLeads.map((lead) => (
                    <div 
                      key={lead.lead_id} 
                      className="flex items-center justify-between p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer"
                      onClick={() => navigate('/leads')}
                      data-testid={`lead-item-${lead.lead_id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
                          <span className="text-cyan-700 font-semibold">
                            {lead.name?.charAt(0)?.toUpperCase() || 'L'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{lead.name || "Unknown"}</p>
                          <p className="text-sm text-slate-500 flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {lead.phone}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <div className="flex items-center gap-2">
                          <Badge className={getStageColor(lead.lifecycle_stage)}>
                            {lead.lifecycle_stage?.replace('_', ' ')}
                          </Badge>
                          <Badge variant="outline" className="capitalize text-xs">{lead.source}</Badge>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-slate-400">
                          <Clock className="w-3 h-3" />
                          {lead.created_at ? format(new Date(lead.created_at), "dd MMM, hh:mm a") : "-"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Lead Sources */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold font-[Manrope]">Lead Sources</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.lead_sources?.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <p>No data yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {stats?.lead_sources?.map((source, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${
                          ['bg-cyan-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500'][index % 5]
                        }`}></div>
                        <span className="text-slate-700 capitalize">{source.source}</span>
                      </div>
                      <span className="font-semibold text-slate-900">{source.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Lead Stages */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-[Manrope]">Lead Pipeline</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {stats?.lead_stages?.map((stage, index) => (
                <div 
                  key={index} 
                  className="p-4 rounded-xl bg-slate-50 text-center"
                  data-testid={`stage-${stage.stage}`}
                >
                  <p className="text-2xl font-bold text-slate-900">{stage.count}</p>
                  <p className="text-sm text-slate-500 capitalize mt-1">{stage.stage?.replace('_', ' ')}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="card-hover cursor-pointer" onClick={() => navigate('/whatsapp')}>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">WhatsApp Center</p>
                <p className="text-sm text-slate-500">Manage conversations</p>
              </div>
            </CardContent>
          </Card>
          <Card className="card-hover cursor-pointer" onClick={() => navigate('/appointments')}>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Appointments</p>
                <p className="text-sm text-slate-500">Schedule consultations</p>
              </div>
            </CardContent>
          </Card>
          <Card className="card-hover cursor-pointer" onClick={() => navigate('/analytics')}>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Analytics</p>
                <p className="text-sm text-slate-500">View insights</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
